"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import { ChevronDown } from "lucide-react";
import type { MenuItem } from "@/lib/nav";

/** A menu item with a small dropdown (desktop) or an indented group (mobile). */
export function NavDropdown({ item, mobile = false, onNavigate }: { item: MenuItem; mobile?: boolean; onNavigate?: () => void }) {
  const ref = useRef<HTMLDetailsElement>(null);

  // Safari doesn't focus a clicked <summary>, so focus leaving is not enough to close it.
  useEffect(() => {
    const closeOutside = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) ref.current?.removeAttribute("open");
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, []);

  return (
    <details
      ref={ref}
      className="group relative"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.currentTarget.removeAttribute("open");
          e.currentTarget.querySelector("summary")?.focus();
        }
      }}
      onBlur={(e) => {
        if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget as Node)) e.currentTarget.removeAttribute("open");
      }}
    >
      <summary
        className={`flex cursor-pointer list-none items-center gap-1 [&::-webkit-details-marker]:hidden ${
          mobile
            ? "text-lg font-medium text-foreground"
            : "whitespace-nowrap text-base font-medium text-muted-foreground transition-colors hover:text-foreground lg:text-lg"
        }`}
      >
        {item.label}
        <ChevronDown aria-hidden className="h-4 w-4 transition-transform group-open:rotate-180" />
      </summary>
      <div
        className={
          mobile
            ? "mt-3 grid gap-3 border-s border-border ps-4"
            : "absolute start-1/2 top-full z-50 mt-4 grid min-w-44 gap-1 rounded-xl border border-border bg-card p-2 shadow-lg ltr:-translate-x-1/2 rtl:translate-x-1/2"
        }
      >
        {item.children?.map((link) => (
          <Link
            key={link.href}
            href={link.href}
            hrefLang={link.hrefLang}
            className={mobile ? "text-lg font-medium text-foreground transition-colors hover:text-primary" : "whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"}
            onClick={() => {
              ref.current?.removeAttribute("open");
              onNavigate?.();
            }}
          >
            {link.label}
          </Link>
        ))}
      </div>
    </details>
  );
}
